<template>
  <BaseModal
    :visible="visible"
    title="Report Lost Item"
    icon-class="fas fa-search-location"
    icon-color-class="yellow"
    size="lg"
    @close="$emit('close')"
  >
    <div class="form-content">
      <p class="form-description">
        Fill out this form to report a lost item. We'll notify you if it's found.
      </p>

      <div class="form-grid">
        <div class="form-group">
          <label>Your Name <span class="required">*</span></label>
          <input
            v-model="form.student_name"
            type="text"
            placeholder="Enter your full name"
          >
        </div>

        <div class="form-group">
          <label>Student ID <span class="required">*</span></label>
          <input
            v-model="form.student_id"
            type="text"
            placeholder="e.g., 2024-00001"
          >
        </div>
      </div>

      <div class="form-group">
        <label>Contact Number <span class="required">*</span></label>
        <input
          v-model="form.contact_number"
          type="tel"
          placeholder="+63 912 345 6789"
        >
      </div>

      <div class="form-grid">
        <div class="form-group">
          <label>Item Category <span class="required">*</span></label>
          <select v-model="form.category">
            <option value="">
              Select category
            </option>
            <option
              v-for="cat in categories"
              :key="cat.id"
              :value="cat.id"
            >
              {{ cat.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label>Location Lost <span class="required">*</span></label>
          <select v-model="form.location">
            <option value="">
              Select location
            </option>
            <option
              v-for="loc in locations"
              :key="loc.id"
              :value="loc.id"
            >
              {{ loc.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label>Item Description <span class="required">*</span></label>
        <textarea
          v-model="form.description"
          rows="3"
          placeholder="Describe the item (color, brand, identifying features)"
        />
      </div>

      <div class="form-group">
        <label>Date Lost</label>
        <input
          v-model="form.date_lost"
          type="date"
        >
      </div>

      <div class="form-group">
        <label>Item Image <span class="optional">(optional)</span></label>
        <div
          class="image-upload-area"
          :class="{ 'has-image': imagePreview }"
          @click="triggerFileInput"
          @dragover.prevent
          @drop.prevent="handleDrop"
        >
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            hidden
            @change="handleFileSelect"
          >
          <div
            v-if="!imagePreview"
            class="upload-placeholder"
          >
            <i class="fas fa-cloud-upload-alt" />
            <p>Click or drag image here</p>
            <span>Supports: JPG, PNG, GIF (max 5MB)</span>
          </div>
          <div
            v-else
            class="image-preview"
          >
            <img
              :src="imagePreview"
              alt="Preview"
            >
            <button
              class="remove-image"
              @click.stop="removeImage"
            >
              <i class="fas fa-times" />
            </button>
          </div>
        </div>
      </div>

      <div class="notice-box">
        <i class="fas fa-info-circle" />
        <div>
          <strong>Important:</strong> Lost items are kept for 30 days.
          Please check with the OSAS office regularly.
        </div>
      </div>
    </div>

    <template #footer>
      <div class="modal-actions">
        <button
          class="btn-cancel"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          class="btn-submit"
          :disabled="fetching"
          @click="$emit('submit')"
        >
          <i :class="['fas', fetching ? 'fa-spinner fa-spin' : 'fa-paper-plane']" />
          {{ fetching ? 'Submitting...' : 'Submit Report' }}
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from './BaseModal.vue'

export default {
  name: 'LostItemModal',
  components: {
    BaseModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    form: {
      type: Object,
      default: () => ({})
    },
    fetching: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      imagePreview: null,
      imageFile: null,
      categories: [
        { id: 'electronics', name: 'Electronics (Phone, Laptop, etc.)' },
        { id: 'documents', name: 'Documents & IDs' },
        { id: 'bags', name: 'Bags & Wallets' },
        { id: 'clothing', name: 'Clothing & Accessories' },
        { id: 'books', name: 'Books & School Supplies' },
        { id: 'other', name: 'Other' }
      ],
      locations: [
        { id: 'main_building', name: 'Main Building' },
        { id: 'library', name: 'Library' },
        { id: 'cafeteria', name: 'Cafeteria' },
        { id: 'gymnasium', name: 'Gymnasium' },
        { id: 'parking', name: 'Parking Area' },
        { id: 'grounds', name: 'School Grounds' },
        { id: 'other', name: 'Other' }
      ]
    }
  },
  watch: {
    visible (newVal) {
      if (!newVal) {
        this.clearImage()
      }
    }
  },
  methods: {
    triggerFileInput () {
      if (!this.imagePreview) {
        this.$refs.fileInput.click()
      }
    },
    handleFileSelect (event) {
      const file = event.target.files[0]
      this.processFile(file)
    },
    handleDrop (event) {
      const file = event.dataTransfer.files[0]
      this.processFile(file)
    },
    processFile (file) {
      if (!file) return

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.$emit('error', 'Please select an image file')
        return
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        this.$emit('error', 'Image size must be less than 5MB')
        return
      }

      this.imageFile = file
      this.form.image = file

      // Create preview
      const reader = new FileReader()
      reader.onload = e => {
        this.imagePreview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    removeImage () {
      this.clearImage()
    },
    clearImage () {
      this.imagePreview = null
      this.imageFile = null
      this.form.image = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    }
  }
}
</script>

<style scoped>
@import '../../../assets/css/landing/modals/modals.css';
</style>
